import { Box, Button } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useUserSettingsContext } from "../../../../contexts/Staffing/UserSettingsContext";
import { useRoleColors } from "../../../../hooks/useRoleColors";
import { FONT_SIZE, INK, NEUTRAL, SURFACE } from "../../../../theme/tokens";
import type { DayItem, ShiftStaffTimes } from "../../../../utils/models";
import { ShiftRow } from "../../../DayViewContent/ShiftRow";
import { useHighLevelTimeline } from "../../../DayViewContent/useTimeline";

interface IProps {
	dayItem: DayItem;
	isTimeLineExpanded: boolean;
	onDayClicked: (date: DateTime) => void;
	searchText: string | null;
	onExpandTimeline: (event: React.MouseEvent<HTMLElement>) => void;
}

const MAX_ROWS = 4;

export const HighLevelTimeline: FC<IProps> = ({ isTimeLineExpanded, onDayClicked, searchText, dayItem, onExpandTimeline }) => {
	const roleColor = useRoleColors();
	const { settings } = useUserSettingsContext();
	const { timelineRows } = useHighLevelTimeline({
		nurses: dayItem.shifts,
		requests: dayItem.requests,
		useRoleGroups: settings.groupRoles,
	});

	const allShiftRows: {
		key: string;
		shiftStaffTimes: ShiftStaffTimes[];
		totalCount: number;
	}[] = Object.keys(timelineRows).flatMap((roleKey: string) =>
		Object.keys(timelineRows[roleKey]).map((rowKey: string) => {
			const shiftStaffTimes = timelineRows[roleKey][+rowKey].map((a) => a.shift);
			const totalCount = shiftStaffTimes.reduce((sum: number, shift: ShiftStaffTimes) => sum + (shift.count || 0) + (shift.requestedCount || 0), 0);

			return {
				key: `${roleKey}-${rowKey}`,
				shiftStaffTimes,
				totalCount,
			};
		}),
	);

	const visibleRows = isTimeLineExpanded ? allShiftRows : allShiftRows.slice(0, MAX_ROWS);
	const remainingCount = allShiftRows.length - MAX_ROWS;

	const isStaffInTimeline = useMemo<ShiftStaffTimes | undefined>(() => {
		if (!searchText) return;

		const hiddenRows = isTimeLineExpanded ? [] : allShiftRows.slice(MAX_ROWS);

		return hiddenRows.flatMap((row) => row.shiftStaffTimes).find((row) => row.names?.some((name) => name.toLowerCase() === searchText.toLowerCase()));
	}, [allShiftRows, searchText, isTimeLineExpanded]);

	return (
		<Box sx={{ pt: "8px" }}>
			{visibleRows.map((row) => (
				<ShiftRow
					key={row.key}
					startTime={dayItem.earliestTime}
					endTime={dayItem.latestTime}
					shiftStaffTimes={row.shiftStaffTimes}
					shiftStart={dayItem.earliestTime}
					onShiftClick={() => onDayClicked(dayItem.date)}
					hoursRange={dayItem.latestTime.diff(dayItem.earliestTime, "hour").hours}
					selectedRoles={[]}
					searchText={searchText}
					isHighLevel={true}
				/>
			))}

			{remainingCount > 0 && !isTimeLineExpanded && (
				<Box sx={{ py: 1 }}>
					<Button
						onClick={onExpandTimeline}
						sx={{
							fontSize: FONT_SIZE.EMPHASIS,
							fontWeight: 500,
							color: INK.SECONDARY,
							border: `1px solid ${alpha(INK.SECONDARY, 0.3)}`,
							width: "100%",
							height: "24px",
							borderRadius: 0,
							bgcolor: isStaffInTimeline ? roleColor(isStaffInTimeline.role).fill : SURFACE,
							"&:hover": {
								bgcolor: NEUTRAL[150],
								border: `1px solid ${INK.SECONDARY}`,
							},
							transition: "background-color 0.2s ease, border-color 0.2s ease",
						}}
					>
						+{remainingCount}
					</Button>
				</Box>
			)}

			{isTimeLineExpanded && (
				<Box sx={{ py: 1, alignItems: "center" }}>
					<Button
						onClick={onExpandTimeline}
						sx={{
							fontSize: FONT_SIZE.EMPHASIS,
							fontWeight: 500,
							color: INK.SECONDARY,
							border: `1px solid ${alpha(INK.SECONDARY, 0.3)}`,
							width: "100%",
							height: "24px",
							borderRadius: 0,
							"&:hover": {
								bgcolor: NEUTRAL[150],
								border: `1px solid ${INK.SECONDARY}`,
							},
							transition: "background-color 0.2s ease, border-color 0.2s ease",
						}}
					>
						Show Less
					</Button>
				</Box>
			)}
		</Box>
	);
};
